import invariant from 'tiny-invariant';

import { Clips } from '@air/api';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';

import { type TranscriptSegmentType } from '~/components/AssetModalV2/Content/Panel/Transcript/types';
import { type ClipTranscriptCache, isTimeBasedPagination, type TimeRange } from '~/swr-hooks/clips/transcript/types';
import {
  type FetchedDataForTimeInterval,
  getDataForTimeInterval,
} from '~/swr-hooks/clips/transcript/utils/getDataForTimeInterval';

type FetchClipTranscriptSegmentsParams = {
  clipId: string;
  workspaceId: string;
  startTimeMs: number;
  endTimeMs: number;
  videoDurationMs: number;
};

export const fetchClipTranscriptSegments = async ({
  clipId,
  workspaceId,
  startTimeMs,
  endTimeMs,
  videoDurationMs,
}: FetchClipTranscriptSegmentsParams): Promise<FetchedDataForTimeInterval<TranscriptSegmentType>> => {
  try {
    const response = await Clips.listTranscriptSegments({
      clipId,
      workspaceId,
      startTimeMs: Math.round(startTimeMs),
      endTimeMs: Math.round(endTimeMs),
    });

    invariant(isTimeBasedPagination(response.pagination), 'Pagination should be time based');

    // If there is no segment that startTimeMs <= segment.startTime && segment.endTime <= endTimeMs
    // then nextStartTimeMs will be equal to startTimeMs and list will be empty.
    if (response.data.length === 0 && response.pagination.nextStartTimeMs === Math.round(startTimeMs)) {
      reportErrorToBugsnag({
        error: new Error('Too short period in fetch transcript segments request'),
        context: 'Incorrect fetch transcript segment request period',
        metadata: { clipId, startTimeMs, endTimeMs },
      });
    }

    return {
      data: response.data,
      previousEndTimeMs: response.pagination.previousEndTimeMs ?? startTimeMs,
      nextStartTimeMs: response.pagination.nextStartTimeMs ?? videoDurationMs,
    };
  } catch (error) {
    const typedError = convertUnknownToError(error);
    reportErrorToBugsnag({
      error: typedError,
      context: 'Failed to fetch transcript segments',
      metadata: { clipId, startTimeMs, endTimeMs },
    });
    throw typedError;
  }
};

type GetClipTranscriptCacheForRangeParams = {
  startTimeMs: number;
  endTimeMs: number;
  initialData: ClipTranscriptCache['data'];
  videoDurationMs: number;
  fetchData: (params: TimeRange) => Promise<FetchedDataForTimeInterval<TranscriptSegmentType>>;
};

export const getClipTranscriptCacheForRange = async ({
  startTimeMs,
  endTimeMs,
  initialData,
  videoDurationMs,
  fetchData,
}: GetClipTranscriptCacheForRangeParams): Promise<ClipTranscriptCache> => {
  const newData = await getDataForTimeInterval({
    startTimeMs,
    endTimeMs,
    initialData,
    fetchData,
  });

  const isAllLoaded = newData.length === 1 && newData[0].startTimeMs === 0 && newData[0].endTimeMs >= videoDurationMs;

  return {
    data: newData,
    hasMore: !isAllLoaded,
    videoDurationMs,
  };
};
